import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProjects } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FolderHeart, 
  Calendar, 
  MapPin, 
  TrendingUp,
  ArrowRight,
  Plus,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export const DashboardPage = () => {
  const { user, isLeader } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalBudget: 0,
    totalRealized: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      
      const totalBudget = response.data.reduce((sum, p) => sum + (p.target_budget || 0), 0);
      const totalRealized = response.data.reduce((sum, p) => sum + (p.total_realized || 0), 0);
      
      setStats({
        totalProjects: response.data.length,
        totalBudget,
        totalRealized
      });
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-imperial">
            Welcome, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="font-body text-muted-foreground mt-2">
            {isLeader() ? 'Manage all wedding projects and finances' : 'View your wedding project details'}
          </p>
        </div>
        {isLeader() && (
          <Button 
            onClick={() => navigate('/projects/new')}
            className="bg-imperial hover:bg-imperial/90"
            data-testid="new-project-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-luxury" data-testid="stats-projects">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                  Active Projects
                </p>
                <p className="font-heading text-3xl font-bold text-foreground mt-2">
                  {stats.totalProjects}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-imperial/10 flex items-center justify-center">
                <FolderHeart className="w-6 h-6 text-imperial" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-luxury" data-testid="stats-budget">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                  Total Budget
                </p>
                <p className="font-heading text-2xl font-bold text-foreground mt-2">
                  {formatCurrency(stats.totalBudget)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-gold" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-luxury" data-testid="stats-realized">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                  Total Realized
                </p>
                <p className="font-heading text-2xl font-bold text-foreground mt-2">
                  {formatCurrency(stats.totalRealized)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-6">
          {isLeader() ? 'All Projects' : 'Your Projects'}
        </h2>

        {projects.length === 0 ? (
          <Card className="card-luxury">
            <CardContent className="p-12 text-center">
              <FolderHeart className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="font-body text-muted-foreground">
                {isLeader() ? 'No projects yet. Create your first wedding project!' : 'No projects assigned to you yet.'}
              </p>
              {isLeader() && (
                <Button 
                  onClick={() => navigate('/projects/new')}
                  className="mt-4 bg-imperial hover:bg-imperial/90"
                >
                  Create Project
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card 
                key={project.id} 
                className="card-luxury group cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="font-heading text-lg text-foreground">
                      {project.bride_name} & {project.groom_name}
                    </CardTitle>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.status === 'Active' 
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span className="font-body text-sm">{formatDate(project.wedding_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span className="font-body text-sm truncate">{project.location}</span>
                  </div>
                  
                  <div className="pt-4 border-t border-warm-grey">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Budget</span>
                      <span className="font-body text-sm font-medium">{formatCurrency(project.target_budget)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Realized</span>
                      <span className="font-body text-sm font-medium text-imperial">{formatCurrency(project.total_realized || 0)}</span>
                    </div>
                  </div>

                  <Button 
                    variant="ghost" 
                    className="w-full mt-2 text-imperial hover:bg-imperial/10 group-hover:bg-imperial/5"
                  >
                    View Details
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
